import type { Candle, SymbolRules } from "../types";

const REST_BASES = [
  "https://fapi.binance.com",
  "https://fapi1.binance.com",
  "https://fapi2.binance.com",
];
const EXCHANGE_CACHE_KEY = "futures-risk.exchange-info.v1";
const EXCHANGE_CACHE_TTL = 6 * 60 * 60 * 1000;

interface BinanceFilter {
  filterType: string;
  tickSize?: string;
  stepSize?: string;
  minQty?: string;
  maxQty?: string;
  notional?: string;
  minNotional?: string;
}

interface BinanceSymbol {
  symbol: string;
  baseAsset: string;
  quoteAsset: string;
  status: string;
  contractType: string;
  filters: BinanceFilter[];
}

interface ExchangeCache {
  savedAt: number;
  rules: SymbolRules[];
}

function readExchangeCache(): SymbolRules[] | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(EXCHANGE_CACHE_KEY) ?? "null") as ExchangeCache | null;
    if (!parsed || Date.now() - parsed.savedAt > EXCHANGE_CACHE_TTL || !Array.isArray(parsed.rules)) return null;
    return parsed.rules;
  } catch {
    return null;
  }
}

function writeExchangeCache(rules: SymbolRules[]): void {
  try {
    localStorage.setItem(EXCHANGE_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), rules } satisfies ExchangeCache));
  } catch {
    // 缓存失败不影响实时计算，隐私模式或存储已满时直接跳过。
  }
}

async function fetchJson<T>(path: string, timeoutMs = 8000): Promise<T> {
  let lastError: unknown;
  for (const base of REST_BASES) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(`${base}${path}`, {
        signal: controller.signal,
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json() as T;
    } catch (error) {
      lastError = error;
    } finally {
      window.clearTimeout(timeout);
    }
  }
  throw new Error(`币安公开行情暂时不可用：${lastError instanceof Error ? lastError.message : "网络错误"}`);
}

function filterOf(symbol: BinanceSymbol, types: string[]): BinanceFilter | undefined {
  return symbol.filters.find((filter) => types.includes(filter.filterType));
}

function mapSymbolRules(symbol: BinanceSymbol): SymbolRules {
  const price = filterOf(symbol, ["PRICE_FILTER"]);
  const lot = filterOf(symbol, ["LOT_SIZE"]);
  const marketLot = filterOf(symbol, ["MARKET_LOT_SIZE"]);
  const notional = filterOf(symbol, ["MIN_NOTIONAL", "NOTIONAL"]);
  const steps = [Number(lot?.stepSize), Number(marketLot?.stepSize)].filter((value) => Number.isFinite(value) && value > 0);
  const minimums = [Number(lot?.minQty), Number(marketLot?.minQty)].filter((value) => Number.isFinite(value) && value > 0);
  const maximums = [Number(lot?.maxQty), Number(marketLot?.maxQty)].filter((value) => Number.isFinite(value) && value > 0);
  return {
    symbol: symbol.symbol,
    baseAsset: symbol.baseAsset,
    quoteAsset: symbol.quoteAsset,
    status: symbol.status,
    priceTick: Number(price?.tickSize ?? 0.00000001),
    // 采用限价单和市价单都能接受的更严格步长/范围，减少复制到币安后被拒绝的概率。
    quantityStep: steps.length ? Math.max(...steps) : 0.00000001,
    minQuantity: minimums.length ? Math.max(...minimums) : 0,
    maxQuantity: maximums.length ? Math.min(...maximums) : 0,
    minNotional: Number(notional?.notional ?? notional?.minNotional ?? 0),
  };
}

export async function fetchExchangeRules(force = false): Promise<SymbolRules[]> {
  if (!force) {
    const cached = readExchangeCache();
    if (cached) return cached;
  }
  const info = await fetchJson<{ symbols: BinanceSymbol[] }>("/fapi/v1/exchangeInfo", 12000);
  const rules = info.symbols
    .filter((symbol) => symbol.contractType === "PERPETUAL" && symbol.quoteAsset === "USDT")
    .map(mapSymbolRules)
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
  writeExchangeCache(rules);
  return rules;
}

export async function fetchTickerPrice(symbol: string): Promise<number> {
  const encoded = encodeURIComponent(symbol.toUpperCase());
  const payload = await fetchJson<{ price: string }>(`/fapi/v2/ticker/price?symbol=${encoded}`);
  const price = Number(payload.price);
  if (!Number.isFinite(price) || price <= 0) throw new Error("币安返回了无效价格");
  return price;
}

export async function fetchKlines(symbol: string, interval = "15m", limit = 300): Promise<Candle[]> {
  const encoded = encodeURIComponent(symbol.toUpperCase());
  const rows = await fetchJson<Array<[number, string, string, string, string]>>(
    `/fapi/v1/klines?symbol=${encoded}&interval=${encodeURIComponent(interval)}&limit=${limit}`,
    12000,
  );
  return rows.map((row) => ({
    time: Math.floor(row[0] / 1000),
    open: Number(row[1]),
    high: Number(row[2]),
    low: Number(row[3]),
    close: Number(row[4]),
  }));
}

export function subscribeKline(
  symbol: string,
  interval: string,
  onCandle: (candle: Candle) => void,
  onStatus?: (connected: boolean) => void,
): () => void {
  const stream = `${symbol.toLowerCase()}@kline_${interval}`;
  const socket = new WebSocket(`wss://fstream.binance.com/ws/${stream}`);
  socket.addEventListener("open", () => onStatus?.(true));
  socket.addEventListener("close", () => onStatus?.(false));
  socket.addEventListener("error", () => onStatus?.(false));
  socket.addEventListener("message", (event) => {
    try {
      const payload = JSON.parse(String(event.data)) as {
        k?: { t: number; o: string; h: string; l: string; c: string };
      };
      if (!payload.k) return;
      onCandle({
        time: Math.floor(payload.k.t / 1000),
        open: Number(payload.k.o),
        high: Number(payload.k.h),
        low: Number(payload.k.l),
        close: Number(payload.k.c),
      });
    } catch {
      // 忽略单条无法解析的推送，后续 K 线仍可继续更新。
    }
  });
  return () => socket.close(1000, "view changed");
}

export const FALLBACK_RULES: SymbolRules = {
  symbol: "BTWUSDT",
  baseAsset: "BTW",
  quoteAsset: "USDT",
  status: "MANUAL",
  priceTick: 0.00001,
  quantityStep: 1,
  minQuantity: 1,
  maxQuantity: 1_000_000,
  minNotional: 5,
};
